import {
  useEffect,
  useState,
} from 'react';

import Dynamicore, { SERVICES } from 'services/dynamicore';
import { getCurrentSessionData } from 'utils/aws/cognito';
import { getSignedUrl, upload } from 'utils/aws/s3';

import { toBase64 } from '../constants';
import useDragAndDrop from './useDragAndDrop';

//Manejo de carga de archivos, conversion PDF, extraccion de texto y manejo de errores para la edicion del archivo
export default function useFileUpload({
  defaultFile,
  defaultFileName,
  name,
  onChange,
  events,
  accept,
  base64,
  error,
  isMobile,
  getTextFromPDF,
  company,
  setSignatures,
  handleSidebarToggle,
}) {
  const [text, setText] = useState('');
  const [currentError, setCurrentError] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [originFile, setOriginFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showMobileAlert, setShowMobileAlert] = useState(false);

  //Carga de archivo por defecto
  const loadDefaultFile = async function () {
    if (defaultFile !== undefined) {
      setIsUploading(true);
      const renamedFile = new File([defaultFile], defaultFileName, {
        type: defaultFile.type,
        lastModified: defaultFile.lastModified,
      });

      setText(defaultFileName);

      const target = {
        name,
        value: renamedFile,
      };

      if (defaultFile.name.split('.').pop() === 'pdf') {
        const fileUrl = URL.createObjectURL(renamedFile);
        setPdfFile(fileUrl);
        setOriginFile(null);
      } else {
        setOriginFile(URL.createObjectURL(renamedFile));
        setPdfFile(await transformDocToPdf(defaultFile));
      }
      onChange({
        target,
      });

      if (events?.onChange) {
        events.onChange(target);
      }
      setIsUploading(false);
    }
  };

  const transformDocToPdf = async (file) => {
    try {
      const currentSessionData = getCurrentSessionData();
      const { Location: url } = await upload(
        `company/${company?.id}/documents/${file?.name}`,
        file,
        {
          company: company?.id,
          user: currentSessionData?.userData?.UserAttributes?.sub,
        },
        company?.id,
      );
      const { name } = await Dynamicore(SERVICES.DOC_TO_PDF).post({
        url,
      });

      return await getSignedUrl(name, 1000);
    } catch (error) {
      console.error('Error al convertir a PDF:', error);
      setCurrentError('Error al convertir el archivo a PDF');
      return URL.createObjectURL(file);
    }
  };

  //Cargar archivo
  const handleFileChange = async function ({ target: { files } }) {
    setCurrentError(false);
    setIsUploading(true);
    const names = [];
    let filesOutput = [];
    const validExt = accept.map((_) => _?.toLowerCase());

    if (!base64) {
      filesOutput = files;
    }

    try {
      for await (const file of files) {
        const ext = (file.name.split('.').pop() || '').toLowerCase();

        names.push(file.name);

        if (base64) {
          filesOutput.push(await toBase64(file));
        }

        if (!currentError && validExt.length) {
          setCurrentError(validExt.includes(ext) ? '' : 'Invalid file type');
        }

        if (file.type === 'application/pdf') {
          const fileUrl = URL.createObjectURL(file);
          setPdfFile(fileUrl);
          setOriginFile(null);
          await getTextFromPDF(fileUrl);
          handleSidebarToggle(0, true);
        } else {
          const signedUrl = await transformDocToPdf(file);
          setPdfFile(signedUrl);
          setOriginFile(URL.createObjectURL(file));
          await getTextFromPDF(signedUrl);
          handleSidebarToggle(0, true);
        }
      }
    } catch (error) {
      console.debug('handleFileChange', error);
    }

    setText(names.join(', '));

    const target = {
      name,
      value: filesOutput[0],
    };

    onChange({
      target,
    });

    if (events?.onChange) {
      events.onChange(target);
    }

    setSignatures([]);
    setIsUploading(false);
  };

  //Descargar PDF
  const handleDownloadPdf = () => {
    if (pdfFile) {
      const link = document.createElement('a');
      link.href = originFile || pdfFile;
      link.download = text; // Puedes cambiar el nombre del archivo aquí
      link.click();
    }
  };

  const {
    isDragging,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useDragAndDrop({
    onFiles: handleFileChange,
  });

  //Carga de archivo por defecto
  useEffect(() => {
    loadDefaultFile();
  }, [defaultFile]);

  useEffect(() => {
    // Para mostrar el Alert en moviles
    if (pdfFile && isMobile) {
      setShowMobileAlert(true);
    }
  }, [pdfFile, isMobile]);

  //Manejo de error
  useEffect(() => {
    setCurrentError(error);
  }, [error]);

  return {
    text,
    currentError,
    pdfFile,
    isUploading,
    showMobileAlert,
    isDragging,
    setShowMobileAlert,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDownloadPdf,
  };
}
